import { lazy as reactLazy, type ComponentType } from "react";

const RELOAD_KEY = "amplify:chunk-reload";

/**
 * React.lazy with resilience against failed dynamic imports.
 *
 * A chunk request can fail transiently (flaky network, or a new deploy that
 * removed the hashed file the current tab is asking for). We retry once after a
 * short delay, then fall back to a single full page reload so the browser picks
 * up the fresh asset manifest. The reload is guarded by sessionStorage so a
 * genuinely broken chunk can never cause a reload loop.
 */
export function lazyWithRetry<T extends ComponentType<never>>(
  factory: () => Promise<{ default: T }>,
) {
  return reactLazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      try {
        await new Promise((r) => setTimeout(r, 400));
        const mod = await factory();
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch (retryErr) {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
          // Keep the promise pending while the page navigates away.
          return new Promise<{ default: T }>(() => {});
        }
        throw retryErr;
      }
    }
  });
}
